import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import { FaClock, FaPlus, FaEdit, FaTimes, FaTrash } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { apiGet, apiPost, apiDelete } from '../../../shared/utils/apiConfig';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';

const dayNamesAr = {
  saturday: 'السبت',
  sunday: 'الأحد',
  monday: 'الإثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة'
};

function formatDay(key) {
  if (!key) return '-';
  const lower = String(key).toLowerCase();
  return dayNamesAr[lower] || key;
}

const DoctorDaySlots = () => {
  const { setTitle } = useTitle();
  const [clinics, setClinics] = useState([]);
  const [workDays, setWorkDays] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [selectedWorkDayId, setSelectedWorkDayId] = useState('');
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newSlotTime, setNewSlotTime] = useState('');
  const [newSlotClinicId, setNewSlotClinicId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingSlots, setPendingSlots] = useState([]); // { time, clinicId }
  const [editingSlot, setEditingSlot] = useState(null);
  const [editClinicId, setEditClinicId] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, slotId: null, workDayId: null });
  const [editModal, setEditModal] = useState({ isOpen: false });
  const [deletingWorkDay, setDeletingWorkDay] = useState(false);

  const getClinicDisplayName = (clinicIdentifier) => {
    if (!clinicIdentifier) return 'عيادة';
    const targetClinic = clinics.find((clinic) => String(clinic.id ?? clinic.clinic_id) === String(clinicIdentifier));
    if (!targetClinic) return `عيادة رقم ${clinicIdentifier}`;
    return (
      targetClinic.title ||
      targetClinic.name ||
      targetClinic.clinic_name ||
      targetClinic?.clinic?.title ||
      targetClinic?.clinic?.name ||
      `عيادة رقم ${clinicIdentifier}`
    );
  };

  const getWorkDayLabel = (dayId) => {
    const targetDay = workDays.find((day) => String(day.id) === String(dayId));
    if (!targetDay) {
      return `يوم رقم ${dayId}`;
    }
    return formatDay(targetDay.day || targetDay.name) || `يوم رقم ${dayId}`;
  };

  useEffect(() => {
    setTitle('مواعيد العمل');
  }, [setTitle]);

  useEffect(() => {
    if (clinics.length > 0 && !newSlotClinicId) {
      setNewSlotClinicId(String(clinics[0].id ?? clinics[0].clinic_id ?? ''));
    }
  }, [clinics, newSlotClinicId]);

  useEffect(() => {
    let isMounted = true;
    const fetchClinics = async () => {
      try {
        const data = await apiGet('/doctor/my_clinics');
        if (!isMounted) return;
        const list = data?.result || data?.data || [];
        setClinics(Array.isArray(list) ? list : []);
        if (Array.isArray(list) && list.length > 0) {
          setSelectedClinicId(String(list[0].id ?? list[0].clinic_id ?? ''));
        }
      } catch (err) {
        if (!isMounted) return;
        showErrorToast('فشل تحميل العيادات');
      }
    };
    fetchClinics();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let mountedRef = { current: true };
    const fetchWorkDays = async () => {
      if (!selectedClinicId) return;
      try {
        const data = await apiGet('/doctor_dash/doctor_work_days', { clinic_id: selectedClinicId });
        if (!mountedRef.current) return;
        const list = data?.result || data?.data || [];
        setWorkDays(Array.isArray(list) ? list : []);
        if (Array.isArray(list) && list.length > 0) {
          const firstId = String(list[0].id);
          setSelectedWorkDayId(firstId);
        } else {
          setSelectedWorkDayId('');
        }
      } catch (err) {
        if (!mountedRef.current) return;
        showErrorToast('فشل تحميل أيام العمل');
      }
    };
    fetchWorkDays();
    return () => {
      mountedRef.current = false;
    };
  }, [selectedClinicId]);

  useEffect(() => {
    const mountedRef = { current: true };
    const fetchSlots = async () => {
      if (!selectedWorkDayId) { setSlots([]); return; }
      try {
        setLoading(true);
        const data = await apiGet(`/doctor_dash/doctor_day_slots/${selectedWorkDayId}`);
        if (!mountedRef.current) return;
        const list = data?.result || data?.data || [];
        setSlots(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!mountedRef.current) return;
        showErrorToast('فشل تحميل المواعيد');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    fetchSlots();
    return () => { mountedRef.current = false; };
  }, [selectedWorkDayId]);

  const handleGenerateSlots = async () => {
    if (!selectedWorkDayId) {
      showErrorToast('اختر يوم العمل أولاً');
      return;
    }

    const slotsArray = pendingSlots.length > 0
      ? pendingSlots.map((s) => ({ clinic_id: Number(s.clinicId), slot_time: s.time }))
      : (newSlotTime && newSlotClinicId ? [{ clinic_id: Number(newSlotClinicId), slot_time: newSlotTime }] : []);

    if (slotsArray.length === 0) {
      showErrorToast('أضف ميعادًا واحدًا على الأقل');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        doctor_day_id: Number(selectedWorkDayId),
        slots: slotsArray
      };

      const res = await apiPost('/doctor_dash/generate_day_slots', payload);
      showSuccessToast(res?.message || `تم إنشاء المواعيد بنجاح ليوم ${getWorkDayLabel(selectedWorkDayId)}`);

      setNewSlotTime('');
      setPendingSlots([]);

      try {
        const data = await apiGet(`/doctor_dash/doctor_day_slots/${selectedWorkDayId}`);
        const list = data?.result || data?.data || [];
        setSlots(Array.isArray(list) ? list : []);
      } catch (refreshError) {
        showErrorToast('فشل تحميل المواعيد');
      }
    } catch (err) {
      showErrorToast('فشل إنشاء المواعيد');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSlot = async (slotId) => {
    if (!slotId || !editClinicId) {
      showErrorToast('من فضلك اختر العيادة');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        slot_id: Number(slotId),
        clinic_id: Number(editClinicId)
      };
      const res = await apiPost('/doctor_dash/edit_slot', payload);
      showSuccessToast(res?.message || 'تم تعديل الميعاد بنجاح');
      setEditingSlot(null);
      setEditClinicId('');
      setEditModal({ isOpen: false });
      // Refresh slots
      const refreshSlots = async () => {
        try {
          const data = await apiGet(`/doctor_dash/doctor_day_slots/${selectedWorkDayId}`);
          const list = data?.result || data?.data || [];
          setSlots(Array.isArray(list) ? list : []);
        } catch (err) {
          showErrorToast('فشل تحميل المواعيد');
        }
      };
      await refreshSlots();
    } catch (err) {
      showErrorToast('فشل تعديل الميعاد');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (slot) => {
    const sid = slot.id || slot.slot_id;
    setEditingSlot(sid);
    setEditClinicId(String(slot.clinic_id || ''));
    setEditModal({ isOpen: true });
  };

  const cancelEdit = () => {
    setEditingSlot(null);
    setEditClinicId('');
    setEditModal({ isOpen: false });
  };

  const removePendingSlot = (index) => {
    setPendingSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteWorkDay = async () => {
    if (!deleteModal.workDayId) return;
    
    try {
      setDeletingWorkDay(true);
      await apiGet(`/doctor_dash/delete_day_work/${deleteModal.workDayId}`);
      showSuccessToast('تم حذف يوم العمل بنجاح');
      
      // إعادة تحميل أيام العمل
      const data = await apiGet('/doctor_dash/doctor_work_days', { clinic_id: selectedClinicId });
      const list = data?.result || data?.data || [];
      setWorkDays(Array.isArray(list) ? list : []);
      
      // إعادة تعيين اليوم المختار
      if (Array.isArray(list) && list.length > 0) {
        setSelectedWorkDayId(String(list[0].id));
      } else {
        setSelectedWorkDayId('');
        setSlots([]);
      }
      
      setDeleteModal({ isOpen: false, slotId: null, workDayId: null });
    } catch (err) {
      showErrorToast('فشل حذف يوم العمل');
    } finally {
      setDeletingWorkDay(false);
    }
  };

  return (
    <div className="container-fluid">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="mb-0">مواعيد العمل</h5>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">اختر العيادة</label>
              <select
                className="form-select"
                value={selectedClinicId}
                onChange={(e) => setSelectedClinicId(e.target.value)}
              >
                <option value="">اختر العيادة</option>
                {clinics.map((c) => (
                  <option key={c.id ?? c.clinic_id} value={String(c.id ?? c.clinic_id)}>
                    {c.title || c.name || c.clinic_name || `عيادة رقم ${c.id ?? c.clinic_id}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">اختر اليوم</label>
              <select
                className="form-select"
                value={selectedWorkDayId}
                onChange={(e) => setSelectedWorkDayId(e.target.value)}
                disabled={!workDays.length}
              >
                <option value="">اختر اليوم</option>
                {workDays.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {formatDay(d.day || d.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Add New Slot Form */}
      {selectedWorkDayId && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaPlus />
              <h6 className="mb-0">إضافة ميعاد جديد</h6>
            </div>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">اليوم الحالي لإضافة المواعيد</label>
                <div
                  className="alert alert-primary bg-primary-subtle border-0 text-primary"
                  role="alert"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    direction: 'rtl',
                    textAlign: 'right'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <strong style={{ display: 'block', fontSize: '1rem' }}>
                      {selectedWorkDayId ? getWorkDayLabel(selectedWorkDayId) : 'لم يتم اختيار يوم'}
                    </strong>
                    <span style={{ display: 'block', marginTop: '0.35rem', color: '#4b5563', fontSize: '0.9rem' }}>
                      يمكنك تغيير اليوم من القائمة بالأعلى ثم إضافة مواعيد جديدة.
                    </span>
                  </div>
                  <FaClock style={{ fontSize: '1.4rem' }} />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">الوقت</label>
                <input
                  type="time"
                  className="form-control"
                  value={newSlotTime}
                  onChange={(e) => setNewSlotTime(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">العيادة</label>
                <select
                  className="form-select"
                  value={newSlotClinicId}
                  onChange={(e) => setNewSlotClinicId(e.target.value)}
                >
                  <option value="">اختر العيادة</option>
                  {clinics.map((c) => (
                    <option key={c.id ?? c.clinic_id} value={String(c.id ?? c.clinic_id)}>
                      {c.title || c.name || c.clinic_name || `عيادة رقم ${c.id ?? c.clinic_id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-3 d-flex justify-content-center gap-2">
              <button
                type="button"
                className="btn d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  if (!newSlotTime || !newSlotClinicId) { showErrorToast('اختر الوقت والعيادة'); return; }
                  setPendingSlots((prev) => [...prev, { time: newSlotTime, clinicId: newSlotClinicId }]);
                  setNewSlotTime('');
                }}
                style={{ backgroundColor: '#17a2b8', color: 'white' }}
              >
                <FaPlus /> إضافة للقائمة
              </button>
              <button
                type="button"
                className="btn d-flex align-items-center justify-content-center gap-2"
                onClick={handleGenerateSlots}
                disabled={submitting || (pendingSlots.length === 0 && (!newSlotTime || !newSlotClinicId))}
                style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
              >
                حفظ المواعيد
              </button>
            </div>
            {pendingSlots.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {pendingSlots.map((ps, idx) => (
                  <span key={idx} className="badge bg-secondary d-flex align-items-center" style={{ gap: '0.5rem' }}>
                    {ps.time} - {getClinicDisplayName(ps.clinicId)}
                    <button type="button" className="btn btn-sm btn-light" onClick={() => removePendingSlot(idx)}>
                      <FaTimes />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Slots List */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
            <FaClock />
            <h6 className="mb-0">معاينة المواعيد لليوم المختار</h6>
            </div>
            {selectedWorkDayId && (
              <button
                className="btn btn-sm d-flex align-items-center gap-2"
                onClick={() => setDeleteModal({ isOpen: true, slotId: null, workDayId: selectedWorkDayId })}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
                disabled={deletingWorkDay}
                title="حذف يوم العمل"
              >
                {deletingWorkDay ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <FaTrash /> حذف اليوم
                  </>
                )}
              </button>
            )}
          </div>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : !slots.length ? (
            <p className="text-muted mb-0">لا توجد مواعيد</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-modern">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الوقت</th>
                    <th>العيادة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((s, idx) => {
                    const slotId = s.id || s.slot_id;
                    const isEditing = editingSlot === slotId;
                    return (
                      <tr key={slotId || idx}>
                        <td>{idx + 1}</td>
                        <td>
                          <span className="badge bg-info text-white">
                            {s.time || s.slot_time || s.slot || '-'}
                          </span>
                        </td>
                        <td>
                          <span>
                            {getClinicDisplayName(s.clinic_id)}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm"
                            onClick={() => startEdit(s)}
                            style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
                          >
                            <FaEdit /> تعديل
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, slotId: null, workDayId: null })}
        onConfirm={handleDeleteWorkDay}
        title="تأكيد الحذف"
        message={deleteModal.workDayId ? "هل أنت متأكد من حذف يوم العمل هذا؟ سيتم حذف جميع المواعيد المرتبطة به." : "هل أنت متأكد من حذف هذا الميعاد؟"}
        confirmText="حذف"
        cancelText="إلغاء"
        type="danger"
      />

  {/* Edit Slot Modal (clinic only) */}
  {editModal.isOpen && (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">تعديل الميعاد</h5>
            <button type="button" className="btn-close" onClick={cancelEdit} />
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">العيادة</label>
              <select
                className="form-select"
                value={editClinicId}
                onChange={(e) => setEditClinicId(e.target.value)}
              >
                <option value="">اختر العيادة</option>
                {clinics.map((c) => (
                  <option key={c.id ?? c.clinic_id} value={String(c.id ?? c.clinic_id)}>
                    {c.title || c.name || c.clinic_name || `عيادة رقم ${c.id ?? c.clinic_id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={cancelEdit}>إلغاء</button>
            <button
              type="button"
              className="btn"
              onClick={() => handleEditSlot(editingSlot)}
              disabled={submitting || !editClinicId}
              style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
            >
              حفظ
            </button>
          </div>
        </div>
      </div>
    </div>
  )}
    </div>
  );
};

export default DoctorDaySlots;



